import { useState, useCallback, useEffect } from 'react';

const SESSION_KEY = 'wayra_event_views';

interface UseEventViewTriggerOptions {
  /** Number of event views required to trigger. @default 3 */
  threshold?: number;
  /** Whether the trigger is enabled. @default true */
  enabled?: boolean;
}

interface UseEventViewTriggerReturn {
  /** Whether the threshold has been reached */
  triggered: boolean;
  /** Call this when the user views an event listing */
  recordView: () => void;
  /** Current number of event views recorded this session */
  viewCount: number;
}

/** Read the persisted view count from sessionStorage. */
function getStoredCount(): number {
  if (typeof window === 'undefined') return 0;
  const stored = sessionStorage.getItem(SESSION_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

/**
 * Tracks event listing views within a session and triggers
 * when the user has viewed enough events to indicate research behavior.
 * Used by the behavioral popup (Popup 2).
 *
 * Uses sessionStorage so the count resets when the tab closes.
 * The count continues to increment past the threshold so analytics
 * always reflects the true number of events viewed.
 */
function useEventViewTrigger(
  options: UseEventViewTriggerOptions = {},
): UseEventViewTriggerReturn {
  const { threshold = 3, enabled = true } = options;

  const [count, setCount] = useState(getStoredCount);
  const [triggered, setTriggered] = useState(
    () => enabled && getStoredCount() >= threshold,
  );

  /** Increment the count unconditionally so viewCount stays accurate. */
  const recordView = useCallback(() => {
    if (!enabled) return;

    setCount((prev) => {
      const next = prev + 1;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_KEY, String(next));
      }
      return next;
    });
  }, [enabled]);

  /** Flip triggered once the threshold is crossed. */
  useEffect(() => {
    if (enabled && count >= threshold && !triggered) {
      setTriggered(true);
    }
  }, [count, threshold, enabled, triggered]);

  return {
    triggered,
    recordView,
    viewCount: count,
  };
}

export { useEventViewTrigger };
